import { useState } from 'react';
import { Printer, Check, Trash2 } from '../../lib/icons';
import { toast } from 'sonner';
import { validerTransfert, annulerOrdreTransfert, estBlocageFiliere } from '../../lib/dataProvider';
import { fmt } from '../shared/badges';
import { useAuth } from '../../lib/auth';
import { estAdmin } from '../../lib/permissions';

const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';
const td = 'px-3 py-2 text-sm text-slate-800';

// Étape 2 : validation au tonnage réel + historique complet des mouvements.
export const TransfertsHistorique = ({ transferts, onDone, onImprimer }) => {
  const { user } = useAuth();
  const [reel, setReel] = useState({});
  const planifies = transferts.filter((t) => t.statut === 'Planifié');
  const clos = transferts.filter((t) => t.statut !== 'Planifié');

  const valider = async (t, override = false) => {
    const { error, valeur } = await validerTransfert(t.id, reel[t.id], { utilisateur: user?.name, override });
    if (error) {
      if (
        !override &&
        estBlocageFiliere(error) &&
        estAdmin(user?.role) &&
        window.confirm(`${error}\n\nOVERRIDE ADMINISTRATEUR : forcer quand même ? L'opération sera tracée dans le journal d'audit.`)
      )
        return valider(t, true);
      return toast.error(error);
    }
    toast.success(`${t.numero} validé — ${fmt(reel[t.id], 1)} kg transférés (valorisés ${fmt(valeur, 3)} TND au CMP source)`);
    setReel((r) => ({ ...r, [t.id]: '' }));
    onDone();
  };

  const annuler = async (t) => {
    const { error } = await annulerOrdreTransfert(t.id, user?.name);
    if (error) return toast.error(error);
    toast.success(`Ordre ${t.numero} annulé`);
    onDone();
  };

  return (
    <section data-testid="transferts-historique" className="border border-slate-200 bg-white">
      <div className="border-b border-slate-200 px-4 py-3">
        <h2 className="font-heading text-base font-bold text-slate-900">
          Transferts inter-cuves{' '}
          <span className="font-data text-xs text-slate-500">
            ({planifies.length} ordre(s) en attente de validation · {clos.filter((t) => t.statut === 'Terminé').length} terminé(s))
          </span>
        </h2>
        <p className="text-xs text-slate-500">
          Étape 1 : l'ordre est planifié et imprimé pour l'atelier. Étape 2 : la saisie du tonnage réel
          mouvemente les stocks et clôture l'ordre.
        </p>
      </div>

      {planifies.length > 0 && (
        <div data-testid="transferts-planifies" className="border-b-2 border-amber-400 bg-amber-50/50">
          {planifies.map((t) => (
            <div key={t.id} data-testid={`ordre-${t.id}`} className="flex flex-col gap-2 border-b border-amber-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-data text-sm font-bold text-slate-900">
                  {t.numero} · {t.sourceNom} → {t.cibleNom}
                  <span className="ml-2 border border-amber-500 bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-amber-900">
                    Planifié
                  </span>
                </p>
                <p className="font-data text-[11px] text-slate-600">
                  {t.date} · théorique {fmt(t.qteTheorique, 1)} kg · émis par {t.creePar ?? '—'} · CMP source à l'émission {fmt(t.cmpSource, 3)} TND/kg
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  data-testid={`reel-${t.id}`}
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="Tonnage réel (kg)"
                  value={reel[t.id] ?? ''}
                  onChange={(e) => setReel((r) => ({ ...r, [t.id]: e.target.value }))}
                  className="tabular h-11 w-40 border border-slate-300 bg-white px-2 text-center font-data text-sm outline-none focus:border-slate-900"
                />
                <button
                  type="button"
                  data-testid={`valider-reel-${t.id}`}
                  disabled={!(Number(reel[t.id]) > 0)}
                  onClick={() => valider(t)}
                  className={`flex h-11 items-center gap-1.5 px-3 font-heading text-sm font-bold text-white ${
                    Number(reel[t.id]) > 0 ? 'bg-emerald-700 hover:bg-emerald-800' : 'cursor-not-allowed bg-slate-300'
                  }`}
                >
                  <Check size={15} /> Valider au réel
                </button>
                <button
                  type="button"
                  data-testid={`imprimer-ordre-${t.id}`}
                  onClick={() => onImprimer(t)}
                  className="flex h-11 items-center gap-1.5 border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-900 hover:text-white"
                >
                  <Printer size={15} /> Ordre
                </button>
                <button
                  type="button"
                  data-testid={`annuler-ordre-${t.id}`}
                  onClick={() => annuler(t)}
                  className="flex h-11 items-center border border-slate-300 bg-white px-2.5 text-slate-600 hover:border-red-600 hover:bg-red-600 hover:text-white"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <table className="w-full border-collapse text-left" data-testid="table-transferts">
        <thead className="bg-slate-50">
          <tr>
            {['Date', 'Ordre n°', 'Cuve source', 'Cuve destination', 'Théorique (kg)', 'Réel transféré (kg)', 'CMP source', 'Valeur transférée', 'Statut', 'Validé par', ''].map((c) => (
              <th key={c} className={th}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {clos.length === 0 && (
            <tr><td colSpan={11} className="px-3 py-8 text-center text-sm text-slate-500">Aucun transfert clôturé.</td></tr>
          )}
          {clos.map((t) => (
            <tr key={t.id} data-testid={`transfert-${t.id}`} className={t.statut === 'Annulé' ? 'bg-slate-50 text-slate-400' : ''}>
              <td className={`${td} font-data text-xs`}>{t.valideLe ?? t.date}</td>
              <td className={`${td} font-data font-semibold`}>{t.numero}</td>
              <td className={td}>{t.sourceNom}</td>
              <td className={td}>{t.cibleNom}</td>
              <td className={`${td} tabular font-data text-right`}>{fmt(t.qteTheorique, 1)}</td>
              <td data-testid={`transfert-reel-${t.id}`} className={`${td} tabular font-data text-right font-semibold`}>{t.qteReelle != null ? fmt(t.qteReelle, 1) : '—'}</td>
              <td className={`${td} tabular font-data text-right`}>{fmt(t.cmpSource, 3)}</td>
              <td className={`${td} tabular font-data text-right`}>{t.valeur != null ? `${fmt(t.valeur, 3)} TND` : '—'}</td>
              <td className={td}>
                <span className={`border px-1.5 py-0.5 font-data text-[10px] font-bold uppercase tracking-[0.06em] ${
                  t.statut === 'Terminé' ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-300 bg-slate-100 text-slate-500'
                }`}>
                  {t.statut}
                </span>
                {t.override && <span className="ml-1 border border-red-300 bg-red-50 px-1 font-data text-[10px] font-bold text-red-700">OVERRIDE</span>}
              </td>
              <td className={`${td} text-xs`}>{t.validePar ?? '—'}</td>
              <td className="px-3 py-2 text-right">
                <button
                  type="button"
                  data-testid={`imprimer-transfert-${t.id}`}
                  onClick={() => onImprimer(t)}
                  className="border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-900 hover:text-white"
                >
                  <Printer size={13} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};
